<template>
  <div id="container">
    <TheNavbar />
    
    <div class="accordion-container">
      <div class="title">Nondestructive Testing</div>
      
      <div class="accordion-section collapsed" id="theory-section">
        <div class="accordion-header" @click="expandSection('theory')">
          <span class="triangle">&#9654;</span>
          <span class="title">Theoretical Foundation</span>
        </div>
        <div class="accordion-content" style="display:none;">
          <p>Nondestructive testing (NDT) is based on various physical principles that allow inspection without damaging the test object:</p>
          <ul>
            <li><b>Ultrasonic Testing (UT)</b>: Uses high-frequency sound waves to detect internal flaws. Based on acoustic impedance differences between materials.</li>
            <li><b>Radiographic Testing (RT)</b>: Employs X-rays or gamma rays to reveal internal structures. Different materials absorb radiation at different rates.</li>
            <li><b>Magnetic Particle Testing (MT)</b>: Utilizes magnetic fields to detect surface and near-surface discontinuities in ferromagnetic materials.</li>
            <li><b>Liquid Penetrant Testing (PT)</b>: Uses capillary action to draw penetrant into surface-breaking defects, making them visible under appropriate lighting.</li>
            <li><b>Eddy Current Testing (ET)</b>: Based on electromagnetic induction. Changes in electrical conductivity and permeability affect eddy current flow patterns.</li>
            <li><b>Visual Testing (VT)</b>: Direct or remote visual examination using optical aids to detect surface discontinuities.</li>
          </ul>
        </div>
      </div>
      
      <div class="accordion-section collapsed" id="equipment-section">
        <div class="accordion-header" @click="expandSection('equipment')">
          <span class="triangle">&#9654;</span>
          <span class="title">Equipment</span>
        </div>
        <div class="accordion-content" style="display:none;">
          <p>NDT methods require specialized equipment for each technique:</p>
          <ul>
            <li><b>Ultrasonic Equipment</b>: Ultrasonic flaw detectors, transducers (straight beam, angle beam), couplants, calibration blocks</li>
            <li><b>Radiographic Equipment</b>: X-ray machines, gamma ray sources, film, digital detectors, densitometers, film viewers</li>
            <li><b>Magnetic Particle Equipment</b>: Magnetic yokes, prods, coils, magnetic particles (dry powder, wet suspension), UV lights</li>
            <li><b>Penetrant Testing Equipment</b>: Penetrant chemicals, developers, cleaners, UV lights, white light sources</li>
            <li><b>Eddy Current Equipment</b>: Eddy current instruments, probes (surface, bobbin, array), reference standards</li>
            <li><b>Visual Testing Equipment</b>: Magnifying glasses, borescopes, mirrors, measuring devices, cameras</li>
          </ul>
        </div>
      </div>
      
      <div class="accordion-section collapsed" id="applications-section">
        <div class="accordion-header" @click="expandSection('applications')">
          <span class="triangle">&#9654;</span>
          <span class="title">Applications</span>
        </div>
        <div class="accordion-content" style="display:none;">
          <ul>
            <li><b>Aerospace Industry</b>: Aircraft structural inspections, engine component testing, composite material evaluation</li>
            <li><b>Nuclear Power</b>: Reactor vessel inspections, steam generator tube testing, fuel assembly examinations</li>
            <li><b>Oil & Gas</b>: Pipeline integrity assessments, pressure vessel inspections, offshore platform examinations</li>
            <li><b>Manufacturing</b>: Quality control during production, weld inspections, casting evaluations</li>
            <li><b>Construction & Infrastructure</b>: Bridge inspections, building structural assessments, concrete testing</li>
            <li><b>Automotive</b>: Engine block testing, wheel inspections, safety-critical component evaluations</li>
            <li><b>Railway</b>: Rail track inspections, wheel and axle testing, structural component evaluations</li>
          </ul>
        </div>
      </div>
    </div>
    
    <!-- <TheFooter /> -->
  </div>
</template>

<script>
import TheNavbar from '@/components/TheNavbar.vue'
import TheFooter from '@/components/TheFooter.vue'

export default {
  name: 'NondestructiveTesting',
  components: {
    TheNavbar,
    TheFooter
  },
  methods: {
    expandSection(section) {
      const current = document.getElementById(section + '-section');
      const isExpanded = current.classList.contains('expanded');
      
      // 先收起所有
      document.querySelectorAll('.accordion-section').forEach(sec => {
        sec.classList.add('collapsed');
        sec.classList.remove('expanded');
        sec.querySelector('.accordion-content').style.display = 'none';
        sec.querySelector('.triangle').innerHTML = '&#9654;'; // ▶
      });
      
      // 如果当前是收起的，则展开；如果是展开的，则保持收起
      if (!isExpanded) {
        current.classList.remove('collapsed');
        current.classList.add('expanded');
        current.querySelector('.accordion-content').style.display = '';
        current.querySelector('.triangle').innerHTML = '&#9660;'; // ▼
      }
    }
  }
}
</script>

<style scoped>
.accordion-container {
  padding: 32px;
  max-width: 1200px;
  width: 100%;
  min-height: 700px;
  margin: 32px auto;
  background: #f8fafc;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(60, 80, 120, 0.08);
  color: #263238;
  font-family: 'Segoe UI', 'Arial', sans-serif;
}

.accordion-container > .title {
  font-size: 2.3em;
  font-weight: 700;
  text-align: center;
  margin-bottom: 32px;
  color: #2A439A;
  letter-spacing: 1px;
}

.accordion-section {
  margin-bottom: 16px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(60, 80, 120, 0.04);
  border: 1px solid #e3e8f0;
  transition: box-shadow 0.2s;
}

.accordion-section.expanded {
  box-shadow: 0 4px 16px rgba(60, 80, 120, 0.10);
  border-color: #263238;
}

.accordion-header {
  cursor: pointer;
  padding: 18px 28px;
  font-size: 1.25em;
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #2A439A;
  border-radius: 10px 10px 0 0;
  background: #eee;
  transition: background 0.2s;
}

.accordion-header:hover {
  background: #ddd;
}

.accordion-header .triangle {
  margin-right: 14px;
  font-size: 1.3em;
  transition: transform 0.2s;
}

.accordion-section.expanded .accordion-header .triangle {
  transform: rotate(90deg);
}

.accordion-header .title {
  font-size: 1.2em;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.accordion-content {
  padding: 18px 32px 18px 48px;
  background: #f5faff;
  border-radius: 0 0 10px 10px;
  color: #263238;
  font-size: 1.08em;
  line-height: 1.7;
  border-top: 1px solid #e3e8f0;
}

.accordion-content ul {
  margin: 0 0 0 1.2em;
  padding: 0;
}

.accordion-content li {
  margin-bottom: 0.7em;
}
</style>
